// src/hooks/useUnavailableBlockMove.ts
import { useCallback, useState } from "react";

import { ConflictingAppointment, fetchConflictingAppointments, newlyConflicting } from "../../api/healthcare/Appointment/AppointmentApi";
import { useNotifications } from "../../context/NotificationContext";
import { BlockRef, blockRefKey } from "../../utils/appointments/availability";
import { useUpdateUnavailableBlock } from "./usePractitionerAvailability";
import type { UserUuid } from "../../types/identity";

export const BLOCK_STAYS_WITH_PRACTITIONER = "A block cannot be moved to another practitioner";

export interface BlockMoveTarget {
	start: Date;
	end: Date;
	// The column the drop landed in; null in the week view, which has none.
	toColumnId: UserUuid | null;
}

export interface PendingBlockMove extends BlockMoveTarget {
	ref: BlockRef;
	conflicts: ConflictingAppointment[];
	checkFailed: boolean;
}

export const useUnavailableBlockMove = () => {
	const updateBlock = useUpdateUnavailableBlock();
	const { showWarning } = useNotifications();
	const [pending, setPending] = useState<PendingBlockMove | null>(null);
	const [movingKey, setMovingKey] = useState<string | null>(null);

	const commit = useCallback(
		(ref: BlockRef, start: Date, end: Date) => {
			const key = blockRefKey(ref);
			setMovingKey(key);
			updateBlock.mutate(
				{
					userId: ref.userId,
					blockId: ref.id,
					// ⚠️ UTC INSTANTS with an explicit offset — a naive "2026-01-05 10:00:00" is a 422.
					payload: { starts_at: start.toISOString(), ends_at: end.toISOString(), block_type: ref.blockType, reason: ref.reason },
				},
				{ onSettled: () => setMovingKey((current) => (current === key ? null : current)) }
			);
		},
		[updateBlock]
	);

	const requestMove = useCallback(
		async (ref: BlockRef, move: BlockMoveTarget) => {
			// The server drops user_id from a PUT, so a cross-column drop is not persistable at all.
			if (move.toColumnId !== null && ref.userId !== null && move.toColumnId !== ref.userId) {
				showWarning(BLOCK_STAYS_WITH_PRACTITIONER, "Delete this block and create a new one on the other practitioner.");
				return;
			}

			setMovingKey(blockRefKey(ref));

			let conflicts: ConflictingAppointment[] = [];
			try {
				conflicts = newlyConflicting(await fetchConflictingAppointments(ref.userId, move.start, move.end), ref);
			} catch {
				setPending({ ref, start: move.start, end: move.end, toColumnId: move.toColumnId, conflicts: [], checkFailed: true });
				setMovingKey(null);
				return;
			}

			if (conflicts.length > 0) {
				setPending({ ref, start: move.start, end: move.end, toColumnId: move.toColumnId, conflicts, checkFailed: false });
				setMovingKey(null);
				return;
			}

			commit(ref, move.start, move.end);
		},
		[commit, showWarning]
	);

	const confirmPending = useCallback(() => {
		if (!pending) return;
		commit(pending.ref, pending.start, pending.end);
		setPending(null);
	}, [pending, commit]);

	const cancelPending = useCallback(() => setPending(null), []);

	return { requestMove, pending, confirmPending, cancelPending, movingKey };
};
